import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  OutlinedInput,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import CustomInput from 'app/components/CustomInput';
import CustomSelect from 'app/components/CustomSelect';
import MaskedInput from 'app/components/MaskedInput';
import { aniPayTypes } from 'app/pages/anipay/constants';
import { FormFieldValues } from 'app/pages/anipay/model/types';

import LoaderFullScreen from '../../../../../components/LoaderFullScreen';
import { azerbaijaniEnglishLettersKeyDown } from '../../../../../utils/azerbaijaniEnglishLettersKeyDown';
import { handleCheckKeyFields } from '../../../../../utils/handleCheckKey';
import { numbersOnlyWithoutDotKeyDown } from '../../../../../utils/numberOnlyKeyDown';
import { useGetCustomerType } from '../../../api/usePaymentFlow';

interface Props {
  handleSearch: (business_key: string) => void;
  handleSearchIban?: (business_key: string) => void;
  isLoading: boolean;
  isIbanLoading?: boolean;
  resetPaymentFlowIban?: () => void;
}

// Helper component for search button
const SearchButton = ({
  onClick,
  isLoading,
  disabled,
}: {
  onClick: () => void;
  isLoading: boolean;
  disabled: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Button onClick={onClick} size='small' color='secondary' disabled={disabled}>
      {isLoading ? (
        <CircularProgress sx={{ width: '12px !important', height: '12px !important' }} />
      ) : (
        t('anipay.search')
      )}
    </Button>
  );
};

// Mobile phone input component
const MobileInput = ({
  value,
  errors,
  setValue,
  handleSearch,
  isLoading,
  businessKey,
  isDirty,
}: {
  value: string;
  errors: any;
  setValue: any;
  handleSearch: (business_key: string) => void;
  isLoading: boolean;
  businessKey: string;
  isDirty: boolean;
}) => {
  const { t } = useTranslation();
  const shouldShowError = !!errors.aliasValue?.message;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Typography sx={{ fontSize: '15px', fontWeight: 400, lineHeight: 1 }}>
        {t('anipay.ipsAliasValue')}
      </Typography>
      <MaskedInput
        mask='(99) 999 99 99'
        maskChar={null}
        disabled={false}
        value={value || ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setValue('aliasValue', e.target.value, { shouldValidate: true });
        }}
      >
        <OutlinedInput
          placeholder='(00) 000 00 00'
          error={shouldShowError}
          startAdornment={
            <InputAdornment position='start'>
              <Typography sx={{ fontFamily: 'Yelo', fontWeight: 500 }}>+994</Typography>
            </InputAdornment>
          }
          endAdornment={
            <SearchButton
              onClick={() => handleSearch(businessKey)}
              isLoading={isLoading}
              disabled={!!errors?.aliasValue && isDirty}
            />
          }
          sx={{
            width: '100%',
            '& .MuiInputBase-input': {
              p: 0,
            },
          }}
        />
      </MaskedInput>
      {shouldShowError && (
        <Typography sx={{ color: 'red', fontSize: '14px' }}>
          {errors.aliasValue?.message}
        </Typography>
      )}
    </Box>
  );
};

// IBAN input component
const IbanInput = ({
  register,
  errors,
  isDirty,
  setValue,
  resetPaymentFlowIban,
}: {
  register: any;
  errors: any;
  isDirty: boolean;
  setValue: any;
  resetPaymentFlowIban?: () => void;
}) => {
  const { t } = useTranslation();
  const previousFirstEightRef = useRef<string>('');

  const resetFormFields = () => {
    // Reset all form fields that come after IBAN input
    setValue('clientType', null, { shouldValidate: true });
    setValue('beneficiaryName', null, { shouldValidate: true });
    setValue('beneficiaryTIN', null, { shouldValidate: true });
    setValue('beneficiaryCode', null, { shouldValidate: true });
    setValue('beneficiaryBankName', null, { shouldValidate: true });
    setValue('beneficiaryAccountName', null, { shouldValidate: true });
    setValue('cr_type_id', null, { shouldValidate: true });
    // Reset usePaymentFlowIban result
    resetPaymentFlowIban?.();
  };

  return (
    <CustomInput
      title={t('anipay.ibanTitle')}
      placeholder='AZ00XXXX00000000000000000000'
      disabled={false}
      width='100%'
      rest={{
        ...register('iban', {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            e.target.value = e.target.value.toUpperCase();

            const currentValue = e.target.value;
            const currentFirstEight = currentValue.substring(0, 8);
            const previousFirstEight = previousFirstEightRef.current;

            // Check if first 8 characters have changed and we have a previous value
            if (
              previousFirstEight &&
              currentFirstEight !== previousFirstEight &&
              currentFirstEight.length >= 8
            ) {
              resetFormFields();
            }

            // Update the ref with current first 8 characters
            previousFirstEightRef.current = currentFirstEight;

            return e;
          },
        }),
        maxLength: 28,
      }}
      isValid={!!errors.iban?.message && isDirty}
      errorText={errors.iban?.message}
      onKeyDown={handleCheckKeyFields}
    />
  );
};

// Generic alias input component (for PIN, etc.)
const GenericAliasInput = ({
  register,
  errors,
  selectedAliasType,
  isLoading,
  handleSearch,
  businessKey,
  isDirty,
}: {
  register: any;
  errors: any;
  selectedAliasType: any;
  handleSearch: (business_key: string) => void;
  isLoading: boolean;
  businessKey: string;
  isDirty: boolean;
}) => {
  const { t } = useTranslation();

  const maxLength = aniPayTypes?.find(item => item.value === selectedAliasType?.value)?.maxLength;

  return (
    <CustomInput
      title={t('anipay.ipsAliasValue')}
      placeholder={t('anipay.ipsAliasValue')}
      disabled={false}
      adornment={
        <SearchButton
          onClick={() => handleSearch(businessKey)}
          isLoading={isLoading}
          disabled={!!errors?.aliasValue}
        />
      }
      rest={{
        ...register('aliasValue', {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            if (selectedAliasType?.value === 'PIN') {
              e.target.value = e.target.value.toUpperCase();
            }

            return e;
          },
        }),
        maxLength,
      }}
      isValid={!!errors.aliasValue?.message && isDirty}
      errorText={errors.aliasValue?.message}
    />
  );
};

const GeneralDetails = (props: Props) => {
  const { t } = useTranslation();
  const { handleSearch, handleSearchIban, isLoading, isIbanLoading, resetPaymentFlowIban } = props;

  const { register, control, watch, formState, setValue } = useFormContext<FormFieldValues>();
  const { errors, dirtyFields } = formState;
  const watchShowBenificiaryInfo = watch('bank_name');
  const selectedAliasType = watch('aliasType');
  const aliasValue = watch('aliasValue');
  const selectedClientType = watch('clientType');
  const { data: customerTypeOptions } = useGetCustomerType();

  console.log(errors, 'errorserrorserrorserrors');

  const renderAliasInput = () => {
    if (!selectedAliasType) return null;

    const aliasType = selectedAliasType.value;

    if (aliasType === 'MOBILE') {
      return (
        <MobileInput
          value={aliasValue}
          errors={errors}
          setValue={setValue}
          handleSearch={handleSearch}
          isLoading={isLoading}
          businessKey={customerTypeOptions?.business_key}
          isDirty={dirtyFields.aliasValue}
        />
      );
    }

    if (aliasType === 'IBAN') {
      return (
        <IbanInput
          register={register}
          errors={errors}
          isDirty={dirtyFields.iban}
          setValue={setValue}
          resetPaymentFlowIban={resetPaymentFlowIban}
        />
      );
    }

    return (
      <GenericAliasInput
        register={register}
        errors={errors}
        selectedAliasType={selectedAliasType}
        handleSearch={handleSearch}
        isLoading={isLoading}
        businessKey={customerTypeOptions?.business_key}
        isDirty={dirtyFields.aliasValue}
      />
    );
  };

  const isButtonDisabled =
    !!errors.aliasType?.message ||
    !!errors.aliasValue?.message ||
    !!errors.clientType?.value?.message ||
    !!errors.beneficiaryName?.message ||
    !!errors.beneficiaryTIN?.message;

  return (
    <>
      <LoaderFullScreen open={isIbanLoading} />
      <Grid container spacing={3.5} mb={2}>
        <Grid xs={12} sm={12} md={6} lg={4}>
          <CustomSelect
            width='100%'
            title={t('anipay.ipsAliasType')}
            control={control}
            options={aniPayTypes}
            rest={{ ...register('aliasType') }}
            isValid={!!errors.aliasType?.message}
            errorText={errors.aliasType?.message}
            placeholder={t('anipay.ipsAliasType')}
            onChange={() => {
              setValue('aliasValue', null, { shouldValidate: true });
              setValue('iban', null, { shouldValidate: true });
            }}
          />
        </Grid>
        <Grid xs={12} sm={12} md={6} lg={4}>
          {renderAliasInput()}
        </Grid>
      </Grid>

      {selectedAliasType?.value === 'IBAN' && !errors.iban?.message && (
        <Grid container spacing={3.5}>
          <Grid xs={12} sm={12} md={6} lg={4}>
            <CustomSelect
              title={t('anipay.clientType')}
              width='100%'
              control={control}
              options={customerTypeOptions?.customerType}
              rest={{ ...register('clientType') }}
              isValid={!!errors.clientType?.value?.message}
              errorText={errors.clientType?.value?.message}
              placeholder={t('anipay.clientType')}
              onChange={(value: any) => {
                setValue('beneficiaryName', null, { shouldValidate: true });
                setValue('beneficiaryTIN', null, { shouldValidate: true });
                // Set cr_type_id based on client type
                // '3' for PHYSICAL (Individual) to make documents required
                // Other values for ENTREPRENEUR and JURIDICAL
                setValue('cr_type_id', value?.value, { shouldValidate: true });
              }}
            />
          </Grid>
          {selectedClientType && (
            <>
              <Grid xs={12} sm={12} md={6} lg={4}>
                <CustomInput
                  width='100%'
                  title={t('anipay.beneficiaryName')}
                  placeholder={t('anipay.beneficiaryName')}
                  rest={{ ...register('beneficiaryName') }}
                  disabled={false}
                  isValid={!!errors.beneficiaryName?.message && dirtyFields.beneficiaryName}
                  errorText={errors.beneficiaryName?.message}
                  onKeyDown={azerbaijaniEnglishLettersKeyDown}
                />
              </Grid>
              {selectedClientType?.value !== '3' && (
                <Grid xs={12} sm={12} md={6} lg={4}>
                  <CustomInput
                    width='100%'
                    title={t('anipay.beneficiaryTIN')}
                    placeholder={t('anipay.beneficiaryTIN')}
                    rest={{
                      ...register('beneficiaryTIN'),
                      maxLength: 10,
                    }}
                    disabled={!watch('beneficiaryName')}
                    isValid={!!errors.beneficiaryTIN?.message && dirtyFields.beneficiaryTIN}
                    errorText={errors.beneficiaryTIN?.message}
                    onKeyDown={numbersOnlyWithoutDotKeyDown}
                  />
                </Grid>
              )}
            </>
          )}

          {!!watch('beneficiaryCode') && (
            <>
              <Grid xs={12} sm={12} md={6} lg={4}>
                <CustomInput
                  width='100%'
                  title={t('anipay.beneficiaryCode')}
                  placeholder={t('anipay.beneficiaryCode')}
                  rest={{ ...register('beneficiaryCode') }}
                  disabled={true}
                  isValid={!!errors.beneficiaryCode?.message}
                  errorText={errors.beneficiaryCode?.message}
                />
              </Grid>
              <Grid xs={12} sm={12} md={6} lg={4}>
                <CustomInput
                  width='100%'
                  title={t('anipay.beneficiaryBankName')}
                  placeholder={t('anipay.beneficiaryBankName')}
                  rest={{ ...register('beneficiaryBankName') }}
                  disabled={true}
                  isValid={!!errors.beneficiaryBankName?.message}
                  errorText={errors.beneficiaryBankName?.message}
                />
              </Grid>
              <Grid xs={12} sm={12} md={6} lg={4}>
                <CustomInput
                  width='100%'
                  title={t('anipay.beneficiaryBankAccountNumber')}
                  placeholder={t('anipay.beneficiaryBankAccountNumber')}
                  rest={{ ...register('beneficiaryAccountName') }}
                  disabled={true}
                  isValid={!!errors.beneficiaryAccountName?.message}
                  errorText={errors.beneficiaryAccountName?.message}
                />
              </Grid>
            </>
          )}
        </Grid>
      )}
      {watchShowBenificiaryInfo && selectedAliasType?.value !== 'IBAN' && (
        <>
          <Grid container spacing={3.5} mb={2}>
            <Grid xs={12} sm={12} md={6} lg={4}>
              <CustomInput
                width='100%'
                title={t('anipay.beneficiaryBankName')}
                placeholder={t('anipay.beneficiaryBankName')}
                rest={{ ...register('bank_name'), maxLength: 6 }}
                disabled={true}
                isValid={!!errors.bankCode?.message}
                errorText={errors.bankCode?.message}
              />
            </Grid>
            <Grid xs={12} sm={12} md={6} lg={4}>
              <CustomInput
                width='100%'
                title={t('anipay.beneficiaryBankAccountNumber')}
                placeholder={t('anipay.beneficiaryBankAccountNumber')}
                disabled={true}
                rest={{ ...register('bank_account'), maxLength: 6 }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3.5} mb={2}>
            {watch('cr_tax_no') && (
              <Grid xs={12} sm={12} md={6} lg={4}>
                <CustomInput
                  width='100%'
                  title={t('anipay.beneficiaryTIN')}
                  placeholder={t('anipay.beneficiaryTIN')}
                  disabled={true}
                  rest={{ ...register('cr_tax_no'), maxLength: 6 }}
                />
              </Grid>
            )}
            <Grid xs={12} sm={12} md={6} lg={4}>
              <CustomInput
                width='100%'
                title={t('anipay.beneficiaryName')}
                placeholder={t('anipay.beneficiaryName')}
                disabled={true}
                rest={{ ...register('cr_name'), maxLength: 6 }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3.5} mb={2}>
            <Grid xs={12} sm={12} md={6} lg={4}>
              <CustomInput
                width='100%'
                title={t('anipay.beneficiaryAccountNumber')}
                placeholder={t('anipay.beneficiaryAccountNumber')}
                disabled={true}
                rest={{ ...register('cr_acc_no'), maxLength: 6 }}
              />
            </Grid>
          </Grid>
        </>
      )}
      {watch('clientType') && !watch('beneficiaryCode') && (
        <Button
          sx={{
            ml: 'auto',
            mt: '20px',
          }}
          size='large'
          onClick={() => handleSearchIban(customerTypeOptions?.business_key)}
          disabled={isButtonDisabled}
        >
          {t('commonActions.continue')}
        </Button>
      )}
    </>
  );
};

export default GeneralDetails;
